import { AwsRum, type AwsRumConfig } from "aws-rum-web"

type ConsoleLevel = "log" | "info" | "warn" | "error"
type RecordConsoleEvent = (eventType: string, args: unknown[]) => void

let awsRum: AwsRum | null = null

function setupConsoleOutput(recordConsoleEvent: RecordConsoleEvent, eventType: ConsoleLevel) {
  const originalConsoleFunction = console[eventType].bind(console)
  console[eventType] = ((...args: unknown[]) => {
    originalConsoleFunction(...args)
    recordConsoleEvent(`console_${eventType}`, args)
  }) as Console[ConsoleLevel]
}

function setupConsoleEvents(rum: AwsRum) {
  function setupConsoleEvent(eventType: string, args: unknown[]) {
    rum.recordEvent(eventType, { ...args })
  }

  const events: ConsoleLevel[] = ["log", "info", "warn", "error"]
  events.forEach((eventType) => {
    setupConsoleOutput(setupConsoleEvent, eventType)
  })
}

type InitClientArgs = {
  application_id: string | undefined
  identity_pool_id: string | undefined
  region: string | undefined
}

function initClient({ application_id, identity_pool_id, region }: InitClientArgs) {
  try {
    if (!application_id || !identity_pool_id || !region) {
      throw new Error("AWS RUM environment variables are missing")
    }
    const config: AwsRumConfig = {
      sessionSampleRate: 1,
      identityPoolId: identity_pool_id,
      endpoint: `https://dataplane.rum.${region}.amazonaws.com`,
      telemetries: ["performance", "errors", "http"],
      allowCookies: true,
      enableXRay: false,
    }

    const APPLICATION_VERSION = process.env.NEXT_PUBLIC_PORTAL_VERSION || "unknown"
    awsRum = new AwsRum(application_id, APPLICATION_VERSION, region, config)
    setupConsoleEvents(awsRum)
  } catch (error) {
    console.error("Failed to initialize AWS RUM", error)
  }
}

export const initAwsRum = (): void => {
  if (!awsRum) {
    const domain = window.location.hostname

    if (domain === "localhost") {
      console.warn("AWS RUM is disabled on localhost")
    } else if (domain === process.env.NEXT_PUBLIC_PORTAL_FRONTEND_INTERNAL_DOMAIN_NAME) {
      initClient({
        application_id: process.env.NEXT_PUBLIC_INTERNAL_DOMAIN_RUM_APPLICATION_ID,
        identity_pool_id: process.env.NEXT_PUBLIC_INTERNAL_DOMAIN_RUM_IDENTITY_POOL_ID,
        region: process.env.NEXT_PUBLIC_AWS_DEFAULT_REGION,
      })
    } else if (domain === process.env.NEXT_PUBLIC_PORTAL_FRONTEND_EXTERNAL_DOMAIN_NAME) {
      initClient({
        application_id: process.env.NEXT_PUBLIC_EXTERNAL_DOMAIN_RUM_APPLICATION_ID,
        identity_pool_id: process.env.NEXT_PUBLIC_EXTERNAL_DOMAIN_RUM_IDENTITY_POOL_ID,
        region: process.env.NEXT_PUBLIC_AWS_DEFAULT_REGION,
      })
    } else {
      console.warn("AWS RUM is disabled on unknown domain")
    }
  }
}

export const getAwsRum = (): AwsRum | null => {
  return awsRum
}

export const SetupRumTestError = (): null => {
  if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search)
    const testError = params.get("test_rum_client_error")
    if (testError) {
      console.error(testError)
      throw new Error(testError)
    }
  }
  return null
}
